import { ref, computed, watch, type Ref } from "vue";
import i18n from "@/plugins/i18n";

interface DateRange {
  label: string;
  from: string;
  to: string;
}

export function useDailyDateRange(rangeLength: number = 8) {
  const currentDay = ref<Date>(new Date());

  const baseRangeLength = ref(rangeLength);

  // Watch for prop changes and sync with internal state
  // watch(
  //   () => selectedDay,
  //   (newSelectedDay) => {
  //     console.log("== newSelectedDay", newSelectedDay);
  //     if (newSelectedDay?.date) {
  //       currentDay.value = new Date(newSelectedDay.date);
  //     }
  //   },
  //   { deep: true, immediate: true }
  // );

  // Helper: format date for display (e.g., "4 Aug")
  const formatDateDisplay = (d: Date | string): string => {
    const date = new Date(d);
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
    };
    // Get the current locale from i18n, fallback to "en-US" if not available
    const currentLocale = i18n.locale || "en-US";
    return date.toLocaleDateString(currentLocale, options);
  };

  // Helper: format year for display
  const formatYearDisplay = (d: Date | string): string => {
    const date = new Date(d);
    return date.getFullYear().toString();
  };

  // Helper: get the start of week 1 for a given year
  const getStartOfWeek1 = (year: number): Date => {
    // January 4th is always in week 1
    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay();

    // Calculate the start of week 1 (Monday)
    const diff = jan4.getDate() - jan4Day + (jan4Day === 0 ? -6 : 1);
    const week1Start = new Date(jan4);
    week1Start.setDate(diff);

    return week1Start;
  };

  // Helper: get the last day of week 52 for a given year
  const getLastDayOfWeek52 = (year: number): Date => {
    // Week 52 typically ends around December 28-31
    // Calculate the last day of week 52
    const jan4 = new Date(year, 0, 4); // January 4th is always in week 1
    const jan4Day = jan4.getDay();
    const daysToWeek52Start = (52 - 1) * 7 - jan4Day + 1;
    const week52Start = new Date(jan4);
    week52Start.setDate(jan4.getDate() + daysToWeek52Start);

    // Week 52 ends 6 days after its start
    const week52End = new Date(week52Start);
    week52End.setDate(week52Start.getDate() + 6);

    return week52End;
  };

  // Computed properties for button states
  const isPreviousDisabled = computed(() => {
    const currentDate = new Date(currentDay.value);
    const currentYear = currentDate.getFullYear();

    // Get the start of week 1 for the current year
    const startOfWeek1 = getStartOfWeek1(currentYear);

    currentDate.setHours(0, 0, 0, 0);
    startOfWeek1.setHours(0, 0, 0, 0);

    return currentDate <= startOfWeek1;
  });

  const getDailyRange = (): DateRange => {
    const selectedDate = new Date(currentDay.value);

    // Calculate the start of the week range (7 days starting from selected date)
    const startOfWeek = new Date(selectedDate);

    // Calculate the end of the week range (rangeLength days after start)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + baseRangeLength.value);

    // Format the range display
    const startFormatted = formatDateDisplay(startOfWeek);
    const endFormatted = formatDateDisplay(endOfWeek);

    // Check if both dates are in the same year
    const sameYear = startOfWeek.getFullYear() === endOfWeek.getFullYear();

    let label: string;
    if (sameYear) {
      label = `${startFormatted} - ${endFormatted} ${startOfWeek.getFullYear()}`;
    } else {
      const startYear = formatYearDisplay(startOfWeek);
      const endYear = formatYearDisplay(endOfWeek);

      label = `${startFormatted} ${startYear} - ${endFormatted} ${endYear} `;
    }

    const from = startOfWeek.toISOString().split("T")[0];
    const to = endOfWeek.toISOString().split("T")[0];

    return {
      label: label,
      from: from,
      to: to,
    };
  };

  const previousDay = (): void => {
    const newDate = new Date(currentDay.value);
    newDate.setDate(newDate.getDate() - 1); // Move back one day
    currentDay.value = newDate;
  };

  const nextDay = (): void => {
    const newDate = new Date(currentDay.value);
    newDate.setDate(newDate.getDate() + 1); // Move forward one day
    currentDay.value = newDate;
  };

  const resetToCurrentDay = (): void => {
    currentDay.value = new Date();
  };

  const setDay = (day: any): void => {
    currentDay.value = new Date(day);
  };

  return {
    currentDay,
    isPreviousDisabled,
    getDailyRange,
    previousDay,
    nextDay,
    resetToCurrentDay,
    setDay,
  };
}
